import { Component, HostListener, OnInit, viewChild } from '@angular/core';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService, MenuCategory, ProductCard } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { MenuService } from '../../../core/services/menu.service';
import { CartService } from '../../../core/services/cart.service';
import { RegisterSuccessComponent } from '../modal/register-success/register-success.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, CommonModule, FormsModule, RegisterSuccessComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  isMenuOpen = false;
  isSearchOpen = false;
  showUserMenu = false;
  searchQuery = '';
  searchResults: ProductCard[] = [];
  showLoginModal = false;
  loginView: 'login' | 'register' = 'login';
  loginLoading = false;
  loginError = '';
  loginForm = { email: '', password: '' };
  registerForm = { firstName: '', lastName: '', dni: '', email: '', phone: '', password: '' };

  pwCriteria = { minLength: false, uppercase: false, lowercase: false, number: false, special: false };
  showLoginPassword = false;
  showRegisterPassword = false;
  loginEmailTouched = false;
  registerEmailTouched = false;
  get isPasswordValid(): boolean {
    return this.pwCriteria.minLength && this.pwCriteria.uppercase && this.pwCriteria.lowercase && this.pwCriteria.number && this.pwCriteria.special;
  }

  private searchTimer: any = null;

  readonly dialog = viewChild.required<RegisterSuccessComponent>('dialog');

  constructor(
    private router: Router,
    private apiService: ApiService,
    private menuService: MenuService,
    public authService: AuthService,
    public cartService: CartService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.showUserMenu && !target.closest('.user-menu-wrapper')) {
      this.showUserMenu = false;
    }
    if (this.isSearchOpen && !target.closest('.search-inline') && !target.closest('.search-icon')) {
      this.isSearchOpen = false;
      this.searchQuery = '';
      this.searchResults = [];
    }
  }

  ngOnInit(): void {
    this.menuService.load();
  }

  get menuCategories(): MenuCategory[] {
    return this.menuService.menuData();
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  toggleSearch(): void {
    this.isSearchOpen = !this.isSearchOpen;
    if (!this.isSearchOpen) {
      this.searchQuery = '';
      this.searchResults = [];
    }
  }

  onSearchChange(): void {
    clearTimeout(this.searchTimer);
    if (!this.searchQuery.trim()) {
      this.searchResults = [];
      return;
    }
    this.searchTimer = setTimeout(() => {
      this.apiService.searchProducts({ name: this.searchQuery.trim(), pageSize: 10 }).subscribe({
        next: res => { this.searchResults = res.data.list; }
      });
    }, 400);
  }

  selectProduct(product: ProductCard): void {
    this.isSearchOpen = false;
    this.searchQuery = '';
    this.searchResults = [];
    this.router.navigate(['/products', product.publicId]);
  }

  goToCategory(category: MenuCategory): void {
    this.isMenuOpen = false;
    this.router.navigate(['/lista-productos', category.slug]);
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  doLogin(): void {
    if (!this.loginForm.email || !this.loginForm.password) return;
    if (!this.isValidEmail(this.loginForm.email)) {
      this.loginError = 'Ingrese un correo electrónico válido';
      return;
    }
    this.loginLoading = true;
    this.loginError = '';
    this.authService.login(this.loginForm).subscribe({
      next: () => {
        this.loginLoading = false;
        this.showLoginModal = false;
      },
      error: (err) => {
        this.loginLoading = false;
        this.loginError = err.error?.message || 'Error al iniciar sesión';
      }
    });
  }

  doRegister(): void {
    if (!this.isValidEmail(this.registerForm.email)) {
      this.loginError = 'Ingrese un correo electrónico válido';
      return;
    }
    this.loginLoading = true;
    this.loginError = '';
    this.authService.register(this.registerForm).subscribe({
      next: () => {
        this.loginLoading = false;
        this.showLoginModal = false;
        this.onOpenModal();
      },
      error: (err) => {
        this.loginLoading = false;
        this.loginError = err.error?.message || 'Error al registrarse';
      }
    });
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
  }

  onlyLetters(event: KeyboardEvent): void {
    const regex = /[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/;
    if (!regex.test(event.key)) {
      event.preventDefault();
    }
  }

  onlyNumbers(event: KeyboardEvent): void {
    const regex = /[0-9]/;
    if (!regex.test(event.key)) {
      event.preventDefault();
    }
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  checkPasswordCriteria(): void {
    const pw = this.registerForm.password;
    this.pwCriteria = {
      minLength: pw.length >= 8,
      uppercase: /[A-Z]/.test(pw),
      lowercase: /[a-z]/.test(pw),
      number: /[0-9]/.test(pw),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw),
    };
  }

  protected onOpenModal(): void {
        this.dialog().onShowModal();
    }
}
